import { usePassiveApiActivity } from '../../hooks/usePassiveApiActivity';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AgentSessionDetail,
  AgentSessionSummary,
  AgentTurn,
  ChatStreamEvent,
  RequestedScope,
  SearchResult,
  apiService,
  getApiErrorMessage,
} from '../../services/api';
import {
  getStreamRetryDelayMs,
  isRetryableStreamRequestError,
} from '../../services/idempotency';
import {
  ensureAgenticCacheMeta,
  loadCachedAgenticSummaries,
  persistAgenticSummaries,
  removeAgenticCacheSession,
} from './cache';
import {
  collectThreadVisibleResults,
  mapSessionDetail,
  mapSessionSummary,
  patchDetailResults,
} from './mappers';
import {
  AgenticLiveTurnState,
  AgenticSessionSummaryView,
  AgenticStoreController,
  AgenticStoreState,
  AgenticToolCallState,
} from './types';
import { hasRequestedSearchScope } from './scope';

const SESSION_LIST_STALE_MS = 60 * 1000;
const PENDING_STREAM_STORAGE_PREFIX = 'agentic-chat-pending-stream';
const PENDING_STREAM_REUSE_MAX_AGE_MS = 5 * 60 * 1000;
const MAX_STREAM_RESUME_ATTEMPTS = 2;

type PendingStreamSubmission = {
  sessionId: string;
  idempotencyKey: string;
  message: string;
  scope: RequestedScope;
  createdAt: string;
};

const normalizeDraftScope = (
  selectedIndexes: string[] | null,
  selectedRunIds: string[] | null,
): RequestedScope => ({
  index_ids: selectedIndexes || [],
  prompt_run_ids: selectedRunIds || [],
});

const defaultState = (
  draftScope: RequestedScope,
  activeSessionId: string | null,
): AgenticStoreState => ({
  sessionSummaries: [],
  sessionDetailsById: {},
  activeSessionId,
  draftScope,
  activeTurn: null,
  syncStatus: {
    sessions: 'idle',
    lastSyncedAt: null,
    cacheStatus: 'cold',
  },
  uiSelections: {
    resultSetByTurn: {},
  },
  isLoadingSession: false,
  error: null,
});

const deriveSessionSummaryList = (
  current: AgenticSessionSummaryView[],
  incoming: AgenticSessionSummaryView[],
): AgenticSessionSummaryView[] => {
  const merged = new Map<string, AgenticSessionSummaryView>();
  current.forEach((summary) => merged.set(summary.session_id, summary));
  incoming.forEach((summary) => {
    const existing = merged.get(summary.session_id);
    if (!existing) {
      merged.set(summary.session_id, summary);
      return;
    }
    merged.set(summary.session_id, {
      ...existing,
      ...summary,
      title: summary.title || existing.title || summary.derived_title || existing.derived_title,
      derived_title: summary.title || summary.derived_title || existing.derived_title,
    });
  });

  return Array.from(merged.values()).sort((left, right) => (
    new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime()
  ));
};

const buildOptimisticUserMessage = (message: string, turnId: string) => ({
  id: `optimistic-user:${turnId}`,
  role: 'user' as const,
  turnId,
  content: message,
  timestamp: new Date().toISOString(),
});

const findSummary = (
  summaries: AgenticSessionSummaryView[],
  sessionId: string | null,
): AgenticSessionSummaryView | null => (
  sessionId ? summaries.find((summary) => summary.session_id === sessionId) || null : null
);

const mergeDetailIntoSummary = (
  summary: AgenticSessionSummaryView | null,
  detail: AgentSessionDetail,
): AgenticSessionSummaryView => mapSessionSummary({
  session_id: detail.session_id,
  user_id: detail.user_id,
  created_at: detail.created_at,
  updated_at: detail.updated_at,
  title: detail.title,
  is_active: detail.is_active,
  turn_count: detail.turn_count,
  last_turn_id: detail.last_turn_id,
  last_turn_status: detail.last_turn_status,
}, detail);

const hasDetailResultMemory = (detail: AgentSessionDetail | null | undefined): boolean => (
  Boolean(detail?.turns?.some((turn) => turn.result_sets.some((resultSet) => (resultSet.results || []).length > 0)))
);

const normalizeRequestedScope = (scope: RequestedScope): RequestedScope => ({
  index_ids: [...(scope.index_ids || [])].filter(Boolean).sort(),
  prompt_run_ids: [...(scope.prompt_run_ids || [])].filter(Boolean).sort(),
});

const scopesEqual = (left: RequestedScope, right: RequestedScope): boolean => (
  JSON.stringify(normalizeRequestedScope(left)) === JSON.stringify(normalizeRequestedScope(right))
);

const buildPendingStreamStorageKey = (userId: string, sessionId: string): string => (
  `${PENDING_STREAM_STORAGE_PREFIX}:${userId}:${sessionId}`
);

const generateStreamIdempotencyKey = (): string => {
  if (typeof globalThis !== 'undefined' && typeof globalThis.crypto?.randomUUID === 'function') {
    return `chat-turn:${globalThis.crypto.randomUUID()}`;
  }
  return `chat-turn:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const clearPendingStreamSubmission = (userId: string, sessionId: string): void => {
  try {
    globalThis.sessionStorage?.removeItem(buildPendingStreamStorageKey(userId, sessionId));
  } catch {
    // Ignore storage failures.
  }
};

const isPendingStreamSubmissionFresh = (submission: PendingStreamSubmission): boolean => {
  const createdAt = Date.parse(submission.createdAt);
  if (!Number.isFinite(createdAt)) {
    return false;
  }
  return Date.now() - createdAt <= PENDING_STREAM_REUSE_MAX_AGE_MS;
};

const readPendingStreamSubmission = (
  userId: string,
  sessionId: string,
): PendingStreamSubmission | null => {
  try {
    const raw = globalThis.sessionStorage?.getItem(buildPendingStreamStorageKey(userId, sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed
      || typeof parsed !== 'object'
      || typeof parsed.idempotencyKey !== 'string'
      || typeof parsed.message !== 'string'
      || typeof parsed.sessionId !== 'string'
    ) {
      return null;
    }
    const submission = {
      sessionId: parsed.sessionId,
      idempotencyKey: parsed.idempotencyKey,
      message: parsed.message,
      scope: normalizeRequestedScope(parsed.scope || { index_ids: [], prompt_run_ids: [] }),
      createdAt: typeof parsed.createdAt === 'string' ? parsed.createdAt : new Date().toISOString(),
    };
    if (!isPendingStreamSubmissionFresh(submission)) {
      clearPendingStreamSubmission(userId, sessionId);
      return null;
    }
    return submission;
  } catch {
    return null;
  }
};

const persistPendingStreamSubmission = (
  userId: string,
  submission: PendingStreamSubmission,
): void => {
  try {
    globalThis.sessionStorage?.setItem(
      buildPendingStreamStorageKey(userId, submission.sessionId),
      JSON.stringify({
        ...submission,
        scope: normalizeRequestedScope(submission.scope),
      }),
    );
  } catch {
    // Ignore storage failures; the stream idempotency key is still kept in-memory for this turn.
  }
};

const canReusePendingStreamSubmission = (
  submission: PendingStreamSubmission | null,
  {
    sessionId,
    message,
    scope,
    activeDetail,
  }: {
    sessionId: string;
    message: string;
    scope: RequestedScope;
    activeDetail: AgentSessionDetail | null;
  },
): submission is PendingStreamSubmission => {
  if (!submission) return false;
  if (submission.sessionId !== sessionId || submission.message !== message || !scopesEqual(submission.scope, scope)) {
    return false;
  }
  if (activeDetail?.last_turn_status !== 'processing') {
    return false;
  }
  return true;
};

export const useAgenticSearchController = ({
  userId,
  isActive = true,
  selectedIndexes,
  selectedRunIds,
  initialActiveSessionId,
}: {
  userId: string | null | undefined;
  isActive?: boolean;
  selectedIndexes: string[] | null;
  selectedRunIds: string[] | null;
  initialActiveSessionId: string | null;
}): AgenticStoreController => {
  const [state, setState] = useState<AgenticStoreState>(
    defaultState(normalizeDraftScope(selectedIndexes, selectedRunIds), initialActiveSessionId),
  );
  const canObserve = usePassiveApiActivity(Boolean(state.activeTurn), isActive);
  const canObserveRef = useRef(canObserve);
  canObserveRef.current = canObserve;
  const activeStreamResumeRef = useRef<(() => void) | null>(null);
  const streamRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef(state);
  const currentUserIdRef = useRef<string | null>(userId ?? null);
  const userEpochRef = useRef(0);
  const summaryLoadTokenRef = useRef(0);
  const sessionLoadTokenRef = useRef(0);
  const sessionLoadAbortRef = useRef<AbortController | null>(null);
  const streamTokenRef = useRef(0);
  const activeStreamCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const updateState = useCallback((updater: (prev: AgenticStoreState) => AgenticStoreState) => {
    setState((prev) => {
      const next = updater(prev);
      stateRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const normalizedUserId = userId ?? null;
    if (currentUserIdRef.current === normalizedUserId) return;

    currentUserIdRef.current = normalizedUserId;
    userEpochRef.current += 1;
    summaryLoadTokenRef.current += 1;
    sessionLoadTokenRef.current += 1;
    sessionLoadAbortRef.current?.abort();
    sessionLoadAbortRef.current = null;
    streamTokenRef.current += 1;
    activeStreamCleanupRef.current?.();
    activeStreamCleanupRef.current = null;
    activeStreamResumeRef.current = null;
    updateState(() => defaultState(
      normalizeDraftScope(selectedIndexes, selectedRunIds),
      initialActiveSessionId,
    ));
  // Authentication is the destructive controller boundary. Scope/session
  // updates below must not cancel a live stream or clear warm session state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateState, userId]);

  useEffect(() => {
    const nextDraftScope = normalizeDraftScope(selectedIndexes, selectedRunIds);
    const currentState = stateRef.current;
    if (
      currentState.activeSessionId === initialActiveSessionId
      && scopesEqual(currentState.draftScope, nextDraftScope)
    ) {
      return;
    }
    updateState((prev) => {
      if (
        prev.activeSessionId === initialActiveSessionId
        && scopesEqual(prev.draftScope, nextDraftScope)
      ) {
        return prev;
      }
      return {
        ...prev,
        activeSessionId: initialActiveSessionId,
        draftScope: nextDraftScope,
      };
    });
  }, [initialActiveSessionId, selectedIndexes, selectedRunIds, updateState]);

  useEffect(() => {
    let cancelled = false;
    const userEpoch = userEpochRef.current;
    const isStale = () => cancelled || userEpoch !== userEpochRef.current;

    const hydrateFromCache = async () => {
      if (!userId) {
        updateState(() => defaultState(normalizeDraftScope(selectedIndexes, selectedRunIds), initialActiveSessionId));
        return;
      }

      const cacheStatus = await ensureAgenticCacheMeta(userId);
      if (isStale()) return;

      const cachedSummaries = (await loadCachedAgenticSummaries(userId)).map((summary) => mapSessionSummary(summary));
      if (isStale()) return;

      updateState((prev) => {
        if (userEpoch !== userEpochRef.current) return prev;
        return {
          ...prev,
          sessionSummaries: cachedSummaries,
          syncStatus: {
            ...prev.syncStatus,
            cacheStatus,
          },
        };
      });

    };

    void hydrateFromCache();

    return () => {
      cancelled = true;
    };
  // Cache hydration belongs to the authenticated controller epoch. Draft
  // scope and session switches are handled by the lightweight scope effect
  // and openSession respectively; re-reading IndexedDB for every checkbox or
  // session change made panel transitions visibly noisier.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateState, userId]);

  const loadSessions = useCallback(async (options: { force?: boolean } = {}) => {
    const { force = false } = options;
    if (!userId) return [];
    const userEpoch = userEpochRef.current;

    const now = Date.now();
    const current = stateRef.current;
    const stale = !current.syncStatus.lastSyncedAt
      || now - current.syncStatus.lastSyncedAt > SESSION_LIST_STALE_MS;

    if (!force && current.syncStatus.sessions === 'ready' && !stale && current.sessionSummaries.length > 0) {
      return current.sessionSummaries;
    }

    const loadToken = ++summaryLoadTokenRef.current;
    updateState((prev) => ({
      ...prev,
      syncStatus: {
        ...prev.syncStatus,
        sessions: 'loading',
      },
    }));

    try {
      const remoteSummaries = await apiService.getChatSessions();
      if (loadToken !== summaryLoadTokenRef.current || userEpoch !== userEpochRef.current) {
        return stateRef.current.sessionSummaries;
      }

      const mapped = remoteSummaries.map((summary) =>
        mapSessionSummary(summary, stateRef.current.sessionDetailsById[summary.session_id]),
      );
      await persistAgenticSummaries(userId, remoteSummaries, { replaceMissing: true });
      if (loadToken !== summaryLoadTokenRef.current || userEpoch !== userEpochRef.current) {
        return stateRef.current.sessionSummaries;
      }

      updateState((prev) => ({
        ...prev,
        sessionSummaries: deriveSessionSummaryList(prev.sessionSummaries, mapped),
        syncStatus: {
          ...prev.syncStatus,
          sessions: 'ready',
          lastSyncedAt: Date.now(),
        },
      }));
      return mapped;
    } catch (error) {
      console.error('Failed to load agentic sessions:', error);
      if (loadToken !== summaryLoadTokenRef.current || userEpoch !== userEpochRef.current) {
        return stateRef.current.sessionSummaries;
      }
      updateState((prev) => ({
        ...prev,
        syncStatus: {
          ...prev.syncStatus,
          sessions: 'error',
        },
      }));
      return stateRef.current.sessionSummaries;
    }
  }, [updateState, userId]);

  const upsertDetail = useCallback(async (detail: AgentSessionDetail) => {
    const userEpoch = userEpochRef.current;
    const nextSummary = mergeDetailIntoSummary(findSummary(stateRef.current.sessionSummaries, detail.session_id), detail);
    if (userEpoch !== userEpochRef.current) return;

    // Session details are authoritative, memory-only response data. Commit
    // synchronously inside the caller's generation fence; only the compact
    // summary is allowed to cross the IndexedDB boundary.
    updateState((prev) => ({
      ...prev,
      sessionDetailsById: {
        ...prev.sessionDetailsById,
        [detail.session_id]: mapSessionDetail(detail, prev.uiSelections.resultSetByTurn),
      },
      sessionSummaries: deriveSessionSummaryList(prev.sessionSummaries, [nextSummary]),
      error: prev.activeSessionId === detail.session_id ? null : prev.error,
    }));

    if (userId) {
      await persistAgenticSummaries(userId, [detail].map((session) => ({
        session_id: session.session_id,
        user_id: session.user_id,
        created_at: session.created_at,
        updated_at: session.updated_at,
        title: session.title,
        is_active: session.is_active,
        turn_count: session.turn_count,
        last_turn_id: session.last_turn_id,
        last_turn_status: session.last_turn_status,
      } as AgentSessionSummary)));
    }
  }, [updateState, userId]);

  const openSession = useCallback(async (
    sessionId: string,
  ): Promise<boolean> => {
    if (!userId || !sessionId) return false;

    const userEpoch = userEpochRef.current;
    const loadToken = ++sessionLoadTokenRef.current;
    sessionLoadAbortRef.current?.abort();
    const controller = new AbortController();
    sessionLoadAbortRef.current = controller;

    updateState((prev) => ({
      ...prev,
      activeSessionId: sessionId,
      isLoadingSession: true,
      error: null,
      sessionDetailsById: Object.fromEntries(
        Object.entries(prev.sessionDetailsById).filter(([detailSessionId]) => (
          detailSessionId !== sessionId
        )),
      ),
    }));

    try {
      const detail = await apiService.getChatSession(sessionId, controller.signal);
      if (
        controller.signal.aborted
        || loadToken !== sessionLoadTokenRef.current
        || userEpoch !== userEpochRef.current
      ) return false;
      await upsertDetail(detail);
      if (
        controller.signal.aborted
        || loadToken !== sessionLoadTokenRef.current
        || userEpoch !== userEpochRef.current
      ) return false;
      updateState((prev) => ({
        ...prev,
        activeSessionId: sessionId,
        isLoadingSession: false,
      }));
      return true;
    } catch (error) {
      if (
        controller.signal.aborted
        || loadToken !== sessionLoadTokenRef.current
        || userEpoch !== userEpochRef.current
      ) return false;
      console.error('Failed to load agentic session detail:', error);
      updateState((prev) => ({
        ...prev,
        isLoadingSession: false,
        error: 'Failed to load session.',
      }));
      return false;
    } finally {
      if (sessionLoadAbortRef.current === controller) {
        sessionLoadAbortRef.current = null;
      }
    }
  }, [updateState, upsertDetail, userId]);

  const patchSessionResults = useCallback((
    sessionId: string,
    matcher: (result: SearchResult) => boolean,
    patch: Partial<SearchResult>,
  ) => {
    const currentDetail = stateRef.current.sessionDetailsById[sessionId];
    if (!currentDetail) return;

    const nextDetail = patchDetailResults(currentDetail, matcher, patch);
    if (nextDetail === currentDetail) return;

    updateState((prev) => ({
      ...prev,
      sessionDetailsById: {
        ...prev.sessionDetailsById,
        [sessionId]: nextDetail,
      },
    }));

  }, [updateState]);

  const patchActiveResults = useCallback((matcher: (result: SearchResult) => boolean, patch: Partial<SearchResult>) => {
    const activeSessionId = stateRef.current.activeSessionId;
    if (!activeSessionId) return;
    patchSessionResults(activeSessionId, matcher, patch);
  }, [patchSessionResults]);

  const selectResultSet = useCallback((turnId: string, resultSetId: string | null) => {
    updateState((prev) => {
      const nextSelections = {
        ...prev.uiSelections.resultSetByTurn,
        [turnId]: resultSetId,
      };

      const nextDetails = { ...prev.sessionDetailsById };
      Object.entries(nextDetails).forEach(([sessionId, detail]) => {
        if (!detail.turns.some((turn) => turn.turn_id === turnId)) return;
        nextDetails[sessionId] = mapSessionDetail(detail, nextSelections);
      });

      return {
        ...prev,
        uiSelections: {
          resultSetByTurn: nextSelections,
        },
        sessionDetailsById: nextDetails,
      };
    });
  }, [updateState]);

  const finalizeTurn = useCallback(async (sessionId: string, turn: AgentTurn) => {
    const userEpoch = userEpochRef.current;
    const currentDetail = stateRef.current.sessionDetailsById[sessionId];
    const baseDetail = currentDetail
      ? currentDetail
      : await apiService.getChatSession(sessionId);
    if (userEpoch !== userEpochRef.current) return;

    const baseTurns = baseDetail.turns || [];
    const existingIndex = baseTurns.findIndex((candidate) => candidate.turn_id === turn.turn_id);
    const nextTurns = existingIndex >= 0
      ? baseTurns.map((candidate, index) => (index === existingIndex ? turn : candidate))
      : [...baseTurns, turn];

    const normalizedDetail: AgentSessionDetail = {
      session_id: baseDetail.session_id,
      user_id: baseDetail.user_id,
      created_at: baseDetail.created_at,
      updated_at: turn.updated_at,
      title: baseDetail.title,
      is_active: baseDetail.is_active,
      turn_count: Math.max(baseDetail.turn_count, nextTurns.length),
      last_turn_id: turn.turn_id,
      last_turn_status: turn.status,
      turns: nextTurns,
    };
    await upsertDetail(normalizedDetail);
    if (userId) {
      clearPendingStreamSubmission(userId, sessionId);
    }
    if (userEpoch !== userEpochRef.current) return;
    await loadSessions({ force: true });
  }, [loadSessions, upsertDetail, userId]);

  const refreshSessionAfterStream = useCallback(async (sessionId: string) => {
    const userEpoch = userEpochRef.current;
    await loadSessions({ force: true });
    if (userEpoch !== userEpochRef.current) return;

    if (stateRef.current.activeSessionId === sessionId) {
      await openSession(sessionId);
      return;
    }

    try {
      const detail = await apiService.getChatSession(sessionId);
      if (userEpoch !== userEpochRef.current) return;
      await upsertDetail(detail);
      if (userId && detail.last_turn_status && detail.last_turn_status !== 'processing') {
        clearPendingStreamSubmission(userId, sessionId);
      }
      if (userEpoch !== userEpochRef.current) return;
    } catch (error) {
      console.error('Failed to reconcile background agentic session:', error);
    }
  }, [loadSessions, openSession, upsertDetail, userId]);

  const refreshIfNeededOnFocus = useCallback(async () => {
    const current = stateRef.current;
    const activeSummary = findSummary(current.sessionSummaries, current.activeSessionId);
    const shouldRefreshSummaries = !current.syncStatus.lastSyncedAt
      || Date.now() - current.syncStatus.lastSyncedAt > SESSION_LIST_STALE_MS
      || activeSummary?.last_turn_status === 'processing';

    if (shouldRefreshSummaries) {
      await loadSessions({ force: true });
    }

    const refreshedSummary = findSummary(stateRef.current.sessionSummaries, stateRef.current.activeSessionId);
    const activeDetail = refreshedSummary ? stateRef.current.sessionDetailsById[refreshedSummary.session_id] : null;
    if (
      refreshedSummary
      && (
        refreshedSummary.last_turn_status === 'processing'
        || !activeDetail
        || activeDetail.updated_at !== refreshedSummary.updated_at
      )
    ) {
      await openSession(refreshedSummary.session_id);
    }
  }, [loadSessions, openSession]);

  useEffect(() => () => {
    sessionLoadTokenRef.current += 1;
    sessionLoadAbortRef.current?.abort();
    sessionLoadAbortRef.current = null;
    activeStreamCleanupRef.current?.();
    activeStreamCleanupRef.current = null;
    activeStreamResumeRef.current = null;
    if (streamRetryTimerRef.current !== null) clearTimeout(streamRetryTimerRef.current);
    streamRetryTimerRef.current = null;
    streamTokenRef.current += 1;
  }, []);

  const wasObservingRef = useRef(canObserve);
  useEffect(() => {
    const resumed = canObserve && !wasObservingRef.current;
    wasObservingRef.current = canObserve;
    if (!canObserve) {
      if (streamRetryTimerRef.current !== null) clearTimeout(streamRetryTimerRef.current);
      streamRetryTimerRef.current = null;
      // The task owns execution. Closing this observer cannot cancel the turn.
      activeStreamCleanupRef.current?.();
      activeStreamCleanupRef.current = null;
      return;
    }
    if (!resumed || !userId) return;
    if (stateRef.current.activeTurn && activeStreamResumeRef.current) {
      activeStreamResumeRef.current();
    } else {
      void refreshIfNeededOnFocus();
    }
  }, [canObserve, refreshIfNeededOnFocus, userId]);

  const sendMessage = useCallback(async (rawMessage: string) => {
    const message = rawMessage.trim();
    if (!message || stateRef.current.activeTurn || !userId) return;

    const userEpoch = userEpochRef.current;
    const requestedScope = normalizeDraftScope(selectedIndexes, selectedRunIds);
    const activeSessionId = stateRef.current.activeSessionId;
    const activeDetail = activeSessionId
      ? stateRef.current.sessionDetailsById[activeSessionId] || null
      : null;
    const hasLocalResultMemory = hasDetailResultMemory(activeDetail);
    if (!hasRequestedSearchScope(requestedScope) && !hasLocalResultMemory) {
      updateState((prev) => ({
        ...prev,
        error: 'Select at least one index or extraction run to start a new search, or reopen a session with prior results.',
      }));
      return;
    }

    let sessionId = stateRef.current.activeSessionId;
    if (!sessionId) {
      try {
        const title = message.length <= 100 ? message : `${message.slice(0, 100).trim()}...`;
        let session: AgentSessionSummary;
        try {
          session = await apiService.createChatSession({ title });
        } catch (error: any) {
          if (title && error?.response?.status === 422) {
            session = await apiService.createChatSession({});
          } else {
            throw error;
          }
        }

        if (userEpoch !== userEpochRef.current) return;
        sessionId = session.session_id;
        updateState((prev) => ({
          ...prev,
          activeSessionId: sessionId,
          sessionSummaries: deriveSessionSummaryList(prev.sessionSummaries, [mapSessionSummary(session)]),
          error: null,
        }));
        if (userId) {
          void persistAgenticSummaries(userId, [session]).catch((error) => {
            console.warn('Failed to cache new agentic session summary:', error);
          });
        }
      } catch (error) {
        console.error('Failed to create agentic chat session:', error);
        if (userEpoch !== userEpochRef.current) return;
        updateState((prev) => ({
          ...prev,
          error: getApiErrorMessage(error, 'Failed to start a new conversation.'),
        }));
        throw error;
      }
    }

    if (!sessionId || userEpoch !== userEpochRef.current) return;
    const resolvedSessionId = sessionId;
    const normalizedRequestedScope = normalizeRequestedScope(requestedScope);
    const persistedPendingSubmission = readPendingStreamSubmission(userId, resolvedSessionId);
    const reusePendingSubmission = canReusePendingStreamSubmission(
      persistedPendingSubmission,
      {
        sessionId: resolvedSessionId,
        message,
        scope: normalizedRequestedScope,
        activeDetail,
      },
    );
    if (persistedPendingSubmission && !reusePendingSubmission) {
      clearPendingStreamSubmission(userId, resolvedSessionId);
    }
    const idempotencyKey = reusePendingSubmission
      ? persistedPendingSubmission.idempotencyKey
      : generateStreamIdempotencyKey();

    persistPendingStreamSubmission(userId, {
      sessionId: resolvedSessionId,
      idempotencyKey,
      message,
      scope: normalizedRequestedScope,
      createdAt: reusePendingSubmission
        ? persistedPendingSubmission.createdAt
        : new Date().toISOString(),
    });

    const streamToken = ++streamTokenRef.current;
    const optimisticTurnId = `optimistic:${streamToken}`;
    const optimisticUserMessage = buildOptimisticUserMessage(message, optimisticTurnId);
    const liveTurn: AgenticLiveTurnState = {
      sessionId: resolvedSessionId,
      turnId: null,
      requestedScope,
      optimisticUserMessage,
      agentState: {
        state: 'prepare_turn',
        message: 'Starting turn…',
        progress: 0.05,
        loopIndex: 0,
      },
      toolCalls: [],
      startedAt: new Date().toISOString(),
      streamToken,
    };

    updateState((prev) => ({
      ...prev,
      activeTurn: liveTurn,
      activeSessionId: resolvedSessionId,
      error: null,
    }));

    let sawCompletion = false;
    let sawError = false;
    let reconnectAttempts = 0;
    const seenEventIds = new Set<string>();
    let bootstrapSettled = false;
    let resolveBootstrap: (() => void) | null = null;
    let rejectBootstrap: ((error: unknown) => void) | null = null;
    const bootstrapPromise = new Promise<void>((resolve, reject) => {
      resolveBootstrap = resolve;
      rejectBootstrap = reject;
    });

    const settleBootstrapSuccess = () => {
      if (bootstrapSettled) return;
      bootstrapSettled = true;
      resolveBootstrap?.();
      resolveBootstrap = null;
      rejectBootstrap = null;
    };

    const settleBootstrapFailure = (error: unknown) => {
      if (bootstrapSettled) return;
      bootstrapSettled = true;
      rejectBootstrap?.(error);
      resolveBootstrap = null;
      rejectBootstrap = null;
    };

    const updateToolCalls = (
      toolCalls: AgenticToolCallState[],
      toolName?: string,
      patch?: Partial<AgenticToolCallState>,
    ): AgenticToolCallState[] => {
      if (!toolName || !patch) return toolCalls;
      for (let index = toolCalls.length - 1; index >= 0; index -= 1) {
        if (toolCalls[index].toolName === toolName && toolCalls[index].status === 'started') {
          const next = [...toolCalls];
          next[index] = { ...next[index], ...patch };
          return next;
        }
      }
      return toolCalls;
    };

    const refreshAfterFailure = async () => {
      await refreshSessionAfterStream(resolvedSessionId);
    };

    const clearActiveTurnForStream = (errorMessage?: string) => {
      updateState((prev) => {
        if (
          streamTokenRef.current !== streamToken
          || prev.activeSessionId !== resolvedSessionId
        ) {
          return prev;
        }
        return {
          ...prev,
          activeTurn: prev.activeTurn?.streamToken === streamToken
            ? null
            : prev.activeTurn,
          ...(errorMessage ? { error: errorMessage } : {}),
        };
      });
    };

    const refreshAfterFailureAndClear = (errorMessage?: string) => {
      void refreshAfterFailure().finally(() => {
        // Reconciliation loads the session through openSession(), which clears
        // stale page errors. Re-apply the terminal stream failure afterward so
        // the user is not left with a silently failed turn.
        clearActiveTurnForStream(errorMessage);
      });
    };

    const startStreamConnection = () => {
      if (!canObserveRef.current || streamTokenRef.current !== streamToken || sawCompletion || sawError) return;
      if (streamRetryTimerRef.current !== null) clearTimeout(streamRetryTimerRef.current);
      streamRetryTimerRef.current = null;
      activeStreamCleanupRef.current?.();
      activeStreamCleanupRef.current = apiService.streamChatTurn(
        resolvedSessionId,
        { message, scope: requestedScope },
        (event: ChatStreamEvent) => {
          if (streamTokenRef.current !== streamToken || !canObserveRef.current) return;
          if (event.event_id) {
            if (seenEventIds.has(event.event_id)) {
              return;
            }
            seenEventIds.add(event.event_id);
          }

          switch (event.type) {
            case 'turn_started':
              updateState((prev) => ({
                ...prev,
                activeTurn: prev.activeTurn && prev.activeTurn.streamToken === streamToken
                  ? {
                      ...prev.activeTurn,
                      turnId: event.turn_id,
                      agentState: {
                        ...prev.activeTurn.agentState,
                        message: 'Preparing turn context…',
                        progress: 0.08,
                      },
                    }
                  : prev.activeTurn,
              }));
              break;
            case 'turn_resumed':
              updateState((prev) => ({
                ...prev,
                activeTurn: prev.activeTurn && prev.activeTurn.streamToken === streamToken
                  ? {
                      ...prev.activeTurn,
                      turnId: event.turn_id,
                      agentState: {
                        state: 'prepare_turn',
                        message: event.status === 'completed'
                          ? 'Resuming completed turn…'
                          : event.status === 'failed'
                            ? 'Resuming failed turn…'
                            : 'Resuming in-flight turn…',
                        progress: prev.activeTurn.agentState.progress,
                        loopIndex: prev.activeTurn.agentState.loopIndex,
                      },
                    }
                  : prev.activeTurn,
              }));
              break;
            case 'agent_state':
              updateState((prev) => ({
                ...prev,
                activeTurn: prev.activeTurn && prev.activeTurn.streamToken === streamToken
                  ? {
                      ...prev.activeTurn,
                      turnId: event.turn_id,
                      agentState: {
                        state: event.state,
                        message: event.message,
                        progress: event.progress,
                        loopIndex: event.loop_index,
                      },
                    }
                  : prev.activeTurn,
              }));
              break;
            case 'tool_call_started':
              updateState((prev) => ({
                ...prev,
                activeTurn: prev.activeTurn && prev.activeTurn.streamToken === streamToken
                  ? {
                      ...prev.activeTurn,
                      toolCalls: [
                        ...prev.activeTurn.toolCalls,
                        {
                          id: `${event.turn_id}:${prev.activeTurn.toolCalls.length + 1}:${event.tool_name}`,
                          toolName: event.tool_name,
                          arguments: event.arguments || {},
                          status: 'started',
                          startedAt: new Date().toISOString(),
                        },
                      ],
                    }
                  : prev.activeTurn,
              }));
              break;
            case 'tool_call_result':
              updateState((prev) => ({
                ...prev,
                activeTurn: prev.activeTurn && prev.activeTurn.streamToken === streamToken
                  ? {
                      ...prev.activeTurn,
                      toolCalls: updateToolCalls(
                        prev.activeTurn.toolCalls,
                        event.tool_name,
                        {
                          status: event.result_summary?.error ? 'error' : 'completed',
                          resultSummary: event.result_summary,
                          completedAt: new Date().toISOString(),
                          error: typeof event.result_summary?.error === 'string' ? event.result_summary.error : null,
                        },
                      ),
                    }
                  : prev.activeTurn,
              }));
              break;
            case 'turn_completed':
              sawCompletion = true;
              clearPendingStreamSubmission(userId, resolvedSessionId);
              void finalizeTurn(resolvedSessionId, event.turn)
                .then(() => {
                  if (streamTokenRef.current !== streamToken) return;
                  updateState((prev) => ({
                    ...prev,
                    activeTurn: prev.activeTurn && prev.activeTurn.streamToken === streamToken ? null : prev.activeTurn,
                    error: null,
                  }));
                })
                .catch((error) => {
                  if (streamTokenRef.current !== streamToken) return;
                  console.error('Failed to finalize completed agentic turn:', error);
                  const displayMessage = getApiErrorMessage(
                    error,
                    'Failed to reconcile the completed turn.',
                  );
                  updateState((prev) => ({
                    ...prev,
                    error: displayMessage,
                  }));
                  refreshAfterFailureAndClear(displayMessage);
                });
              break;
            case 'error': {
              const displayMessage = getApiErrorMessage(
                {
                  response: {
                    data: {
                      error: {
                        code: event.error_code,
                        message: event.message,
                        details: event.details,
                      },
                    },
                  },
                },
                event.message,
              );
              sawError = true;
              clearPendingStreamSubmission(userId, resolvedSessionId);
              updateState((prev) => ({
                ...prev,
                activeTurn: prev.activeTurn && prev.activeTurn.streamToken === streamToken
                  ? {
                    ...prev.activeTurn,
                    agentState: {
                      state: 'error',
                      message: displayMessage,
                      progress: prev.activeTurn.agentState.progress,
                      loopIndex: prev.activeTurn.agentState.loopIndex,
                    },
                  }
                  : prev.activeTurn,
                error: displayMessage,
              }));
              refreshAfterFailureAndClear(displayMessage);
              break;
            }
          }
        },
        (error) => {
          if (streamTokenRef.current !== streamToken || !canObserveRef.current) return;
          console.error('Agentic stream failed:', error);
          const canRetry = isRetryableStreamRequestError(error);
          if (canRetry && reconnectAttempts < MAX_STREAM_RESUME_ATTEMPTS) {
            reconnectAttempts += 1;
            const retryDelayMs = getStreamRetryDelayMs(error, 300);
            updateState((prev) => ({
              ...prev,
              activeTurn: prev.activeTurn && prev.activeTurn.streamToken === streamToken
                ? {
                    ...prev.activeTurn,
                    agentState: {
                      ...prev.activeTurn.agentState,
                      message: 'Reconnecting to the in-flight turn…',
                    },
                  }
                : prev.activeTurn,
            }));
            streamRetryTimerRef.current = setTimeout(() => {
              streamRetryTimerRef.current = null;
              if (streamTokenRef.current !== streamToken || sawCompletion || sawError) return;
              startStreamConnection();
            }, retryDelayMs);
            return;
          }
          sawError = true;
          if (!canRetry) {
            clearPendingStreamSubmission(userId, resolvedSessionId);
          }
          settleBootstrapFailure(error);
          const displayMessage = getApiErrorMessage(
            error,
            'Chat stream interrupted.',
          );
          updateState((prev) => ({
            ...prev,
            error: displayMessage,
          }));
          refreshAfterFailureAndClear(displayMessage);
        },
        () => {
          if (streamTokenRef.current !== streamToken || !canObserveRef.current) return;
          activeStreamCleanupRef.current = null;
          if (!sawCompletion && !sawError) {
            if (reconnectAttempts < MAX_STREAM_RESUME_ATTEMPTS) {
              reconnectAttempts += 1;
              streamRetryTimerRef.current = setTimeout(() => {
                streamRetryTimerRef.current = null;
                if (streamTokenRef.current !== streamToken || sawCompletion || sawError) return;
                startStreamConnection();
              }, 300);
              return;
            }
            const interruption = new Error(
              'Chat stream interrupted before it was established.',
            );
            settleBootstrapFailure(interruption);
            refreshAfterFailureAndClear(interruption.message);
          }
        },
        idempotencyKey,
        () => {
          settleBootstrapSuccess();
        },
      );
    };

    activeStreamResumeRef.current = startStreamConnection;
    try {
      startStreamConnection();
      await bootstrapPromise;
    } catch (error) {
      settleBootstrapFailure(error);
      if (!sawError && !sawCompletion) {
        console.error('Failed to start agentic stream:', error);
        updateState((prev) => ({
          ...prev,
          activeTurn: prev.activeTurn && prev.activeTurn.streamToken === streamToken ? null : prev.activeTurn,
          error: getApiErrorMessage(error, 'Failed to start chat stream.'),
        }));
      }
      throw error;
    }
  }, [finalizeTurn, refreshSessionAfterStream, selectedIndexes, selectedRunIds, updateState, userId]);

  const deleteSession = useCallback(async (sessionId: string) => {
    if (!sessionId) return;
    if (stateRef.current.activeTurn?.sessionId === sessionId) {
      activeStreamCleanupRef.current?.();
      activeStreamCleanupRef.current = null;
      streamTokenRef.current += 1;
    }

    await apiService.deleteChatSession(sessionId);
    if (userId) {
      clearPendingStreamSubmission(userId, sessionId);
    }
    if (userId) {
      await removeAgenticCacheSession(userId, sessionId);
    }

    updateState((prev) => {
      const nextDetails = { ...prev.sessionDetailsById };
      delete nextDetails[sessionId];
      return {
        ...prev,
        sessionSummaries: prev.sessionSummaries.filter((summary) => summary.session_id !== sessionId),
        sessionDetailsById: nextDetails,
        activeSessionId: prev.activeSessionId === sessionId ? null : prev.activeSessionId,
        activeTurn: prev.activeTurn?.sessionId === sessionId ? null : prev.activeTurn,
        error: prev.activeSessionId === sessionId ? null : prev.error,
      };
    });
  }, [updateState, userId]);

  const startNewSessionDraft = useCallback(() => {
    updateState((prev) => ({
      ...prev,
      activeSessionId: null,
      error: null,
    }));
  }, [updateState]);

  const activeDetail = useMemo(() => {
    const activeSessionId = state.activeSessionId;
    return activeSessionId ? state.sessionDetailsById[activeSessionId] || null : null;
  }, [state.activeSessionId, state.sessionDetailsById]);

  const activeThreadMessages = useMemo(() => {
    const currentMessages = activeDetail?.thread_messages || [];
    const liveTurn = state.activeTurn;
    if (!liveTurn || liveTurn.sessionId !== state.activeSessionId) return currentMessages;
    return [...currentMessages, liveTurn.optimisticUserMessage];
  }, [activeDetail?.thread_messages, state.activeSessionId, state.activeTurn]);

  const activeResults = useMemo(() => {
    if (!activeDetail) return [];
    return collectThreadVisibleResults(activeDetail.thread_messages);
  }, [activeDetail]);

  const isProcessing = Boolean(state.activeTurn);
  const activeStatusMessage = state.activeTurn?.agentState.message || '';

  return useMemo(() => ({
    state,
    activeDetail,
    activeThreadMessages,
    activeResults,
    isProcessing,
    activeStatusMessage,
    loadSessions,
    openSession,
    startNewSessionDraft,
    sendMessage,
    deleteSession,
    selectResultSet,
    patchActiveResults,
    refreshIfNeededOnFocus,
  }), [
    activeDetail,
    activeResults,
    activeStatusMessage,
    activeThreadMessages,
    deleteSession,
    isProcessing,
    loadSessions,
    openSession,
    patchActiveResults,
    refreshIfNeededOnFocus,
    selectResultSet,
    sendMessage,
    startNewSessionDraft,
    state,
  ]);
};
